import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { entityAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus,
  Building2,
  Edit,
  Trash2,
  MapPin,
  Users
} from 'lucide-react';
import AdminLayout from '../../components/layouts/AdminLayout';

export default function EntitiesList() {
  const navigate = useNavigate();
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEntities();
  }, []);

  const loadEntities = async () => {
    try {
      const response = await entityAPI.getAll();
      setEntities(response.data);
    } catch (error) {
      toast.error('Failed to load communities');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    
    try {
      await entityAPI.delete(id);
      setEntities(entities.filter(e => e.id !== id));
      toast.success('Community deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold">Communities</h1>
            <p className="text-muted-foreground">{entities.length} communities</p>
          </div>
          <Button 
            onClick={() => navigate('/admin/entities/new')}
            className="rounded-full"
            data-testid="add-entity-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Community
          </Button>
        </div>

        {/* List */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : entities.length === 0 ? (
          <div className="empty-state py-20">
            <Building2 className="empty-state-icon" />
            <h3 className="font-heading text-xl font-semibold mb-2">No communities yet</h3>
            <p className="text-muted-foreground mb-4">Add your first apartment complex</p>
            <Button onClick={() => navigate('/admin/entities/new')} className="rounded-full">
              <Plus className="w-4 h-4 mr-2" />
              Add Community
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {entities.map((entity) => (
              <Card key={entity.id} className="card-hover" data-testid={`entity-${entity.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-secondary" />
                      </div>
                      <div>
                        <p className="font-semibold">{entity.name}</p>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {entity.city}
                        </p>
                      </div>
                    </div>
                    <Badge variant={entity.is_active ? 'default' : 'secondary'}>
                      {entity.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-3 truncate">{entity.address}</p>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{entity.pincode}</span>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/admin/entities/${entity.id}/edit`)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(entity.id, entity.name)}
                        className="text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
